import { format, parse, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
/**
 * @file DateUtils.js
 * @description Collection of reusable functions that involve dates and times.
 */

/**
 * Formats a date string into MMM DD YYYY HH:MM T string format with timezone.
 * @param {string} dateString - The date string to format.
 * @returns {string} - The formatted date with month, day, year, time, and timezone (e.g., "Oct 31 2024 10:00 PM UTC").
 * Returns "-" if the input is invalid.
 */
export const formatDateToMmmDDYYYYTimezone = dateString => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate();
    const year = date.getFullYear();
    const timeOptions = { hour: 'numeric', minute: 'numeric', hour12: true };
    const time = date.toLocaleString('en-US', timeOptions);
    const timeZone = date
        .toLocaleString('en-US', { timeZoneName: 'short' })
        .split(' ')
        .pop();
    return `${month} ${day} ${year} ${time} ${timeZone}`;
};

/**
 * Formats a date string into MMM DD YYYY HH:MM T string format without timezone.
 * @param {string} dateString - The date string to format.
 * @returns {string} - The formatted date with month, day, year, time (e.g., "Oct 31 2024 10:00 PM").
 * Returns "-" if the input is invalid.
 */
export const formatDateToMmmDDYYYY = dateString => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate();
    const year = date.getFullYear();
    const timeOptions = { hour: 'numeric', minute: 'numeric', hour12: true };
    const time = date.toLocaleString('en-US', timeOptions);
    return `${month} ${day} ${year} ${time}`;
};

/**
 * Formats a date string into Month DD YYYY HH:MM T string format with timezone.
 * @param {string} dateString - The date string to format.
 * @returns {string} - The formatted date with month, day, year, time, and timezone (e.g., "October 31 2024 10:00 PM UTC").
 * Returns "-" if the input is invalid.
 */
export const formatDateToMonthDDYYYYTimezone = dateString => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const month = date.toLocaleString('en-US', { month: 'long' });
    const day = date.getDate();
    const year = date.getFullYear();
    const timeOptions = { hour: 'numeric', minute: 'numeric', hour12: true };
    const time = date.toLocaleString('en-US', timeOptions);
    const timeZone = date
        .toLocaleString('en-US', { timeZoneName: 'short' })
        .split(' ')
        .pop();
    return `${month} ${day} ${year} ${time} ${timeZone}`;
};

/**
 * Formats a date string into a "MM-DD-YYYY" format with zero-padded month and day.
 * @param {string} dateString - The date string to format.
 * @returns {string} - The formatted date (e.g., "10-31-2024"). Returns "-" if the input is invalid.
 */
export function formatDateToMMDDYYYY(dateString) {
    if (!dateString) return '-';

    const date =
        typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)
            ? parse(dateString, 'yyyy-MM-dd', new Date())
            : new Date(dateString);

    if (Number.isNaN(date.getTime())) {
        return '-';
    }

    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    return `${month}-${day}-${year}`;
}

/**
 * Converts a given UTC date to the users local timezone as a string.
 * @param {string} utcDateString - The UTC date string to convert (ex: "10/13/2025, 4:25:44 PM").
 * @returns {string} - The local date time string converted from UTC to the date time string of the users time zone (ex: "Oct 13 2025 12:25 PM EDT").
 */
export const convertUTCDateTimeToLocalDateTime = utcDateString => {
    if (!utcDateString) return 'N/A';

    const parsedDate = parse(utcDateString, 'M/d/yyyy, h:mm:ss a', new Date());
    if (isNaN(parsedDate)) return 'N/A';

    const utcTime = parsedDate.getTime();
    const timezoneOffset = parsedDate.getTimezoneOffset() * 60000; // Convert minutes to milliseconds to get offset
    const localDate = new Date(utcTime - timezoneOffset);

    const formattedDate = format(localDate, 'MMM d yyyy h:mm a');
    const timeZone = localDate
        .toLocaleTimeString('en-us', { timeZoneName: 'short' })
        .split(' ')
        .pop();

    return `${formattedDate} ${timeZone}`;
};

/**
 * Formats an ISO timestampdate string into the user's local timezone as "MMM DD YYYY HH:MM AM/PM".
 * If the timestamp does not include an explicit timezone, it is treated as UTC (Zulu).
 * @param {string} inputDate - The ISO date string to format.
 * @returns {string} - The formatted local date/time (e.g., "Jun 23 2026 03:23 PM"). Returns "-" if input is empty.
 */
export const formatDate = inputDate => {
    if (!inputDate) return '-';

    const normalizedInput = `${inputDate}`.trim();
    const hasExplicitTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(normalizedInput);
    const assumeUtc = normalizedInput.includes('T') && !hasExplicitTimezone;

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return formatInTimeZone(
        parseISO(assumeUtc ? `${normalizedInput}Z` : normalizedInput),
        userTimeZone,
        'MMM dd yyyy hh:mm a',
    );
};
